/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useThemeObserver } from '@atlaskit/tokens';

// ── Sub-type tile icon imports ──────────────────────────────────────────────
// Dark mode
import areaChartDarkSvg from './assets/darkModeSubtypeIcons/area-chart.svg';
import barChartDarkSvg from './assets/darkModeSubtypeIcons/bar-chart.svg';
import classicDarkSvg from './assets/darkModeSubtypeIcons/classic.svg';
import flipCardsDarkSvg from './assets/darkModeSubtypeIcons/flip-cards.svg';
import flowchartDarkSvg from './assets/darkModeSubtypeIcons/flowchart.svg';
import funnelChartDarkSvg from './assets/darkModeSubtypeIcons/funnel-chart.svg';
import ganttChartDarkSvg from './assets/darkModeSubtypeIcons/gantt-chart.svg';
import geographicalMapDarkSvg from './assets/darkModeSubtypeIcons/geographical-map.svg';
import lineChartDarkSvg from './assets/darkModeSubtypeIcons/line-chart.svg';
import mindmapDarkSvg from './assets/darkModeSubtypeIcons/mindmap.svg';
import modernV2DarkPng from './assets/darkModeSubtypeIcons/modern-v2.png';
import modernDarkSvg from './assets/darkModeSubtypeIcons/modern.svg';
import orgChartDarkSvg from './assets/darkModeSubtypeIcons/org-chart.svg';
import pieChartDarkSvg from './assets/darkModeSubtypeIcons/pie-chart.svg';
import quadrantDarkSvg from './assets/darkModeSubtypeIcons/quadrant.svg';
import sketchV2DarkSvg from './assets/darkModeSubtypeIcons/sketch-v2.svg';
import sketchDarkSvg from './assets/darkModeSubtypeIcons/sketch.svg';
import timelineDarkSvg from './assets/darkModeSubtypeIcons/timeline.svg';
import treemapDarkSvg from './assets/darkModeSubtypeIcons/treemap.svg';
// Light mode
import areaChartSvg from './assets/lightModeSubtypeIcons/area-chart.svg';
import barChartSvg from './assets/lightModeSubtypeIcons/bar-chart.svg';
import classicSvg from './assets/lightModeSubtypeIcons/classic.svg';
import flipCardsSvg from './assets/lightModeSubtypeIcons/flip-cards.svg';
import flowchartSvg from './assets/lightModeSubtypeIcons/flowchart.svg';
import funnelChartSvg from './assets/lightModeSubtypeIcons/funnel-chart.svg';
import ganttChartSvg from './assets/lightModeSubtypeIcons/gantt-chart.svg';
import geographicalMapSvg from './assets/lightModeSubtypeIcons/geographical-map.svg';
import lineChartSvg from './assets/lightModeSubtypeIcons/line-chart.svg';
import mindmapSvg from './assets/lightModeSubtypeIcons/mindmap.svg';
import modernV2Png from './assets/lightModeSubtypeIcons/modern-v2.png';
import modernSvg from './assets/lightModeSubtypeIcons/modern.svg';
import orgChartSvg from './assets/lightModeSubtypeIcons/org-chart.svg';
import pieChartSvg from './assets/lightModeSubtypeIcons/pie-chart.svg';
import quadrantSvg from './assets/lightModeSubtypeIcons/quadrant.svg';
import sketchV2Svg from './assets/lightModeSubtypeIcons/sketch-v2.svg';
import sketchSvg from './assets/lightModeSubtypeIcons/sketch.svg';
import timelineSvg from './assets/lightModeSubtypeIcons/timeline.svg';
import treemapSvg from './assets/lightModeSubtypeIcons/treemap.svg';
import type { KnownSubType } from './types';

// ── Icon components ─────────────────────────────────────────────────────────

const iconStyles = css({
	width: '100%',
	height: '100%',
	objectFit: 'cover',
	display: 'block',
});

const SubTypeIcon = ({ src, srcDark, alt }: { src: string; srcDark: string; alt: string }) => {
	const { colorMode } = useThemeObserver();
	const resolvedSrc = colorMode === 'dark' ? srcDark : src;
	return <img src={resolvedSrc} alt={alt} css={iconStyles} aria-hidden="true" />;
};

// Charts
export const LineChartIcon = (): JSX.Element => (
	<SubTypeIcon src={lineChartSvg} srcDark={lineChartDarkSvg} alt="Line chart" />
);
export const AreaChartIcon = (): JSX.Element => (
	<SubTypeIcon src={areaChartSvg} srcDark={areaChartDarkSvg} alt="Area chart" />
);
export const BarChartIcon = (): JSX.Element => (
	<SubTypeIcon src={barChartSvg} srcDark={barChartDarkSvg} alt="Bar chart" />
);
export const FunnelChartIcon = (): JSX.Element => (
	<SubTypeIcon src={funnelChartSvg} srcDark={funnelChartDarkSvg} alt="Funnel chart" />
);
export const PieChartIcon = (): JSX.Element => (
	<SubTypeIcon src={pieChartSvg} srcDark={pieChartDarkSvg} alt="Pie chart" />
);
export const GanttChartIcon = (): JSX.Element => (
	<SubTypeIcon src={ganttChartSvg} srcDark={ganttChartDarkSvg} alt="Gantt chart" />
);

// Diagrams
export const MindMapIcon = (): JSX.Element => (
	<SubTypeIcon src={mindmapSvg} srcDark={mindmapDarkSvg} alt="Mind map" />
);
export const FlowChartIcon = (): JSX.Element => (
	<SubTypeIcon src={flowchartSvg} srcDark={flowchartDarkSvg} alt="Flowchart" />
);

// Infographics
export const ModernStyleIcon = (): JSX.Element => (
	<SubTypeIcon src={modernSvg} srcDark={modernDarkSvg} alt="Modern style" />
);
export const ModernStyleV2Icon = (): JSX.Element => (
	<SubTypeIcon src={modernV2Png} srcDark={modernV2DarkPng} alt="Modern style" />
);
export const SketchStyleIcon = (): JSX.Element => (
	<SubTypeIcon src={sketchSvg} srcDark={sketchDarkSvg} alt="Sketch style" />
);
export const SketchStyleV2Icon = (): JSX.Element => (
	<SubTypeIcon src={sketchV2Svg} srcDark={sketchV2DarkSvg} alt="Sketch style" />
);
export const ClassicStyleIcon = (): JSX.Element => (
	<SubTypeIcon src={classicSvg} srcDark={classicDarkSvg} alt="Classic style" />
);

// Visual aids
export const FlipCardIcon = (): JSX.Element => (
	<SubTypeIcon src={flipCardsSvg} srcDark={flipCardsDarkSvg} alt="Flip cards" />
);
export const QuadrantIcon = (): JSX.Element => (
	<SubTypeIcon src={quadrantSvg} srcDark={quadrantDarkSvg} alt="Quadrant" />
);
export const TreeMapIcon = (): JSX.Element => (
	<SubTypeIcon src={treemapSvg} srcDark={treemapDarkSvg} alt="Tree map" />
);
export const TimelineIcon = (): JSX.Element => (
	<SubTypeIcon src={timelineSvg} srcDark={timelineDarkSvg} alt="Timeline" />
);
export const GeographicalMapIcon = (): JSX.Element => (
	<SubTypeIcon src={geographicalMapSvg} srcDark={geographicalMapDarkSvg} alt="Geographical map" />
);
export const OrgChartIcon = (): JSX.Element => (
	<SubTypeIcon src={orgChartSvg} srcDark={orgChartDarkSvg} alt="Org chart" />
);

/**
 * Maps each sub-type value to its icon component.
 * Used by the sub-type grid to render the correct icon for each tile.
 *
 * Typed as `Record<KnownSubType, …>` so a missing entry is a compile-time error.
 */
export const SUB_TYPE_ICON_MAP: Record<KnownSubType, () => JSX.Element> = {
	// Charts
	'line-chart': LineChartIcon,
	'area-chart': AreaChartIcon,
	'bar-chart': BarChartIcon,
	'funnel-chart': FunnelChartIcon,
	'pie-chart': PieChartIcon,
	'gantt-chart': GanttChartIcon,
	// Diagrams
	mindmap: MindMapIcon,
	flowchart: FlowChartIcon,
	// Infographics
	'corporate-modern': ModernStyleIcon,
	'corporate-doodle': () =>
		fg('confluence_infographics_enable_new_styles') ? <SketchStyleV2Icon /> : <SketchStyleIcon />,
	charlie: ModernStyleV2Icon,
	minimalist: ClassicStyleIcon,
	// Visual aids
	'flip-cards': FlipCardIcon,
	quadrant: QuadrantIcon,
	'tree-map': TreeMapIcon,
	'horizontal-timeline': TimelineIcon,
	map: GeographicalMapIcon,
	'org-chart': OrgChartIcon,
};
